import { generatePath } from '@ps/web-core-utils';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';

import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

import { PKR_LOADERS } from '../../../constants';
import useGetCurrentClub from '../../../hooks/useGetCurrentClub';
import useSetLoadingOnUnmount from '../../../hooks/useSetLoadingOnUnmount';
import { useHomeGamesWidget } from '../../../model';
import { ClubRoute } from '../../../types/common';
import type { MenuOption } from '../../DropdownMenu/DropdownMenu';
import NoData from '../../NoData/NoData';
import SeasonsDropdown from '../SeasonsDropdown/SeasonsDropdown';

import {
  TXTWCL_No_season_has_been_played_yet,
  TXTWCL_You_will_see_standings_after_t_ELL
} from './Standings.messages.json';
import styles from './Standings.scss';
import type { Props as ListProps } from './StandingsList/StandingsList';
import StandingsList from './StandingsList/StandingsList';
import StandingsSkeleton from './StandingsSkeleton/StandingsSkeleton';
import useSeason from './hooks/useSeason';

const NoResults: React.FC = () => (
  <NoData
    primaryText={<Translation {...TXTWCL_No_season_has_been_played_yet} />}
    secondaryText={<Translation {...TXTWCL_You_will_see_standings_after_t_ELL} />}
  />
);

const Stats: React.FC<{ loading: boolean; props: ListProps }> = ({ loading, props }) => {
  if (loading) return <StandingsSkeleton />;
  return <StandingsList {...props} />;
};

const Standings: React.FC = () => {
  const history = useHistory();
  const { homeGamesClubs = '' } = useSelector(select.cms.getAliases);
  const {
    params: { clubId, subsection }
  } = useRouteMatch<{ clubId: string; subsection: string }>();
  const { clubName } = useGetCurrentClub(clubId);
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget();
  const { standingsPlayMoneyFlag } = useSelector(widgetSelect.uiLayout);
  const stats = useSelector((rootState: RootState) =>
    widgetSelect.clubSeasonStats(rootState, clubId)
  );
  const { clubSeasons: clubSeasonsLoading, clubSeasonStats: clubSeasonStatsLoading } = useSelector(
    widgetSelect.loading
  );
  const { handleSeasonSelect, noSeasons, seasonId, seasonName } = useSeason();
  const loading = clubSeasonsLoading || clubSeasonStatsLoading;

  useSetLoadingOnUnmount(PKR_LOADERS.clubSeasonStats);

  useEffect(() => {
    if (!subsection && seasonId) {
      history.replace(
        generatePath(homeGamesClubs, {
          clubId,
          section: ClubRoute.Standings,
          subsection: seasonId
        })
      );
    }
  }, [clubId, history, homeGamesClubs, seasonId, subsection]);

  useEffect(() => {
    widgetDispatch.fetchClubSeasons({
      clubId
    });
  }, [widgetDispatch, clubId]);

  useEffect(() => {
    if (noSeasons || !seasonId) return;

    widgetDispatch.fetchClubSeasonStats({ clubId, seasonId, isPlayMoney: standingsPlayMoneyFlag });
  }, [widgetDispatch, clubId, seasonId, noSeasons, standingsPlayMoneyFlag]);

  const handleSelect = ({ text, value }: MenuOption) => {
    handleSeasonSelect({ text, value });
    if (value !== subsection) {
      history.replace(
        generatePath(homeGamesClubs, {
          clubId,
          section: ClubRoute.Standings,
          subsection: `${value}`
        })
      );
    }
  };

  if (noSeasons && !loading) {
    return <NoResults />;
  }

  return (
    <div className={styles.standings}>
      <SeasonsDropdown onSelect={handleSelect} value={subsection} />
      <Stats loading={loading} props={{ clubName: `${clubName}`, loading, seasonName, stats }} />
    </div>
  );
};

export default Standings;
